import { useCallback, useEffect, useState } from "react";
import { Save, Loader2, MapPin, ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import PageHeader from "@/components/PageHeader";

export default function VisitSettingsPage() {
  const [settings, setSettings] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const { data } = await api.get("/visits/settings");
    setSettings(data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async (patch) => {
    setBusy(true);
    try {
      const { data } = await api.patch("/visits/settings", patch);
      setSettings(data);
      toast.success("Settings updated");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  if (!settings) return <div className="text-sm text-neutral-500">Loading…</div>;

  return (
    <div className="space-y-4 max-w-2xl" data-testid="visit-settings-page">
      <PageHeader
        breadcrumbs={[{ label: "Admin", to: "/" }, { label: "Masters" }, { label: "Visit settings" }]}
        title="Visit settings"
        subtitle="GPS radius, photo requirements, and outlet coverage rules."
        favorite={{ key: "admin.visit-settings", type: "screen", label: "Visit settings", route: "/admin/visit-settings" }}
        testid="visitsettingspage-page-header"
      />

      <div>
        <h1 className="text-2xl font-bold text-neutral-900">Visit settings</h1>
        <p className="text-sm text-neutral-500">Controls radius enforcement, selfie requirement, and coverage alerting thresholds.</p>
      </div>

      <Card className="border-neutral-200 rounded-2xl">
        <CardContent className="p-5 space-y-5">
          <SettingRow
            label="Visit radius"
            hint="Distance in meters within which a check-in is considered on-outlet."
            control={
              <div className="flex items-center gap-2">
                <Input type="number" min={10} max={5000} className="w-24"
                       defaultValue={settings.visit_radius_m}
                       onBlur={(e) => save({ visit_radius_m: Number(e.target.value) })}
                       data-testid="vs-radius" />
                <span className="text-sm text-neutral-500">m</span>
              </div>
            } />

          <SettingRow
            label="If outside radius"
            hint="Flag lets the visit proceed with a warning. Block prevents check-in when online."
            control={
              <RadioGroup value={settings.on_outside_radius}
                          onValueChange={(v) => save({ on_outside_radius: v })}
                          className="flex items-center gap-4">
                <label className="flex items-center gap-2 text-sm">
                  <RadioGroupItem value="flag" data-testid="vs-outside-flag" /> Flag
                </label>
                <label className="flex items-center gap-2 text-sm">
                  <RadioGroupItem value="block" data-testid="vs-outside-block" /> Block
                </label>
              </RadioGroup>
            } />

          <SettingRow
            label="Start Call hard radius"
            hint="Start Call is blocked outside this distance (metres). The SE must ask a manager for an exception to continue. 0 turns it off."
            control={
              <div className="flex items-center gap-2">
                <Input type="number" min={0} max={5000} className="w-24"
                       defaultValue={settings.start_call_radius_m || 0}
                       onBlur={(e) => save({ start_call_radius_m: Number(e.target.value) })}
                       data-testid="vs-start-call-radius" />
                <span className="text-sm text-neutral-500">m</span>
              </div>
            } />

          <SettingRow
            label="Selfie required on visit start"
            control={
              <Switch checked={settings.require_selfie}
                      onCheckedChange={(v) => save({ require_selfie: v })}
                      data-testid="vs-require-selfie" />
            } />

          <SettingRow
            label="Allow search outside PJP"
            hint="SEs can start visits at outlets not planned for today."
            control={
              <Switch checked={settings.allow_search_outside_pjp}
                      onCheckedChange={(v) => save({ allow_search_outside_pjp: v })}
                      data-testid="vs-outside-pjp" />
            } />

          <SettingRow
            label="Min coverage % (alert threshold)"
            hint="Below this, manager gets a missed-visit alert overnight."
            control={
              <div className="flex items-center gap-2">
                <Input type="number" min={0} max={100} className="w-24"
                       defaultValue={settings.min_coverage_pct}
                       onBlur={(e) => save({ min_coverage_pct: Number(e.target.value) })}
                       data-testid="vs-min-coverage" />
                <span className="text-sm text-neutral-500">%</span>
              </div>
            } />

          <SettingRow
            label="Auto-skip planned outlets after (IST)"
            hint="After this hour, any unvisited planned outlet is marked auto-skipped by the nightly job."
            control={
              <div className="flex items-center gap-2">
                <Input type="number" min={12} max={23} className="w-24"
                       defaultValue={settings.auto_skip_after_hour_ist}
                       onBlur={(e) => save({ auto_skip_after_hour_ist: Number(e.target.value) })}
                       data-testid="vs-auto-skip-hour" />
                <span className="text-sm text-neutral-500">:00</span>
              </div>
            } />
        </CardContent>
      </Card>
    </div>
  );
}

function SettingRow({ label, hint, control }) {
  return (
    <div className="flex items-center justify-between gap-4 flex-wrap">
      <div className="min-w-0">
        <div className="text-sm font-semibold text-neutral-900">{label}</div>
        {hint && <div className="text-xs text-neutral-500 mt-0.5 max-w-md">{hint}</div>}
      </div>
      <div>{control}</div>
    </div>
  );
}
